export type ProjectDeliveryReceipt = {
  jobId: string;
  status:
    | 'pending_confirmation'
    | 'conflict_review'
    | 'project_created'
    | 'rejected';
  projectId: string | null;
  projectDisplayId: string | null;
  reasonCode: string | null;
};

export type ProjectDelivery = {
  status: 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  targets: Array<{
    target: 'dev' | 'prd';
    deliveryStatus:
      | 'pending'
      | 'accepted'
      | 'disabled'
      | 'misconfigured'
      | 'rejected'
      | 'payload_changed'
      | 'unexpected_http_status'
      | 'retry_exhausted'
      | 'retry_disabled'
      | 'dispatch_failed';
    httpStatus: number | null;
    receipt: ProjectDeliveryReceipt | null;
  }>;
};

const object = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const nullableString = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';

/** Keep the optional field compatible with old APIs; never carry provider payloads into UI state. */
export function parseProjectDelivery(
  value: unknown,
): ProjectDelivery | null | undefined {
  if (value === undefined || value === null) return value;
  const invalid = () => new Error('交付推送状态格式异常，请刷新后重试');
  if (
    !object(value) ||
    typeof value.status !== 'string' ||
    !['QUEUED', 'RUNNING', 'COMPLETED', 'FAILED'].includes(value.status) ||
    !Array.isArray(value.targets)
  )
    throw invalid();
  const targets = value.targets.map((target) => {
    if (
      !object(target) ||
      typeof target.target !== 'string' ||
      !['dev', 'prd'].includes(target.target) ||
      typeof target.deliveryStatus !== 'string' ||
      ![
        'pending',
        'accepted',
        'disabled',
        'misconfigured',
        'rejected',
        'payload_changed',
        'unexpected_http_status',
        'retry_exhausted',
        'retry_disabled',
        'dispatch_failed',
      ].includes(target.deliveryStatus) ||
      !(
        target.httpStatus === null ||
        (typeof target.httpStatus === 'number' &&
          Number.isInteger(target.httpStatus) &&
          target.httpStatus >= 100 &&
          target.httpStatus <= 599)
      )
    )
      throw invalid();
    let receipt: ProjectDeliveryReceipt | null = null;
    if (target.receipt !== null) {
      const entry = target.receipt;
      if (
        !object(entry) ||
        typeof entry.jobId !== 'string' ||
        typeof entry.status !== 'string' ||
        ![
          'pending_confirmation',
          'conflict_review',
          'project_created',
          'rejected',
        ].includes(entry.status) ||
        !nullableString(entry.projectId) ||
        !nullableString(entry.projectDisplayId) ||
        !nullableString(entry.reasonCode)
      )
        throw invalid();
      receipt = {
        jobId: entry.jobId,
        status: entry.status as ProjectDeliveryReceipt['status'],
        projectId: entry.projectId,
        projectDisplayId: entry.projectDisplayId,
        reasonCode: entry.reasonCode,
      };
    }
    return {
      target: target.target as 'dev' | 'prd',
      deliveryStatus:
        target.deliveryStatus as ProjectDelivery['targets'][number]['deliveryStatus'],
      httpStatus: target.httpStatus as number | null,
      receipt,
    };
  });
  return { status: value.status as ProjectDelivery['status'], targets };
}
